import { type ReactNode, createContext, useContext, useState } from "react";

import {
  useDataProvider,
  useNotification,
  useParsed,
  useShow,
} from "@refinedev/core";

import { ArtistSelectionModal } from "@/pages/events/features/notifications/components/artist-selection-modal";
import { UpdateConfirmationModal } from "@/pages/events/features/notifications/components/confirmation-modal";
import { FollowerGroupSelectionModal } from "@/pages/events/features/notifications/components/event-announcement-modal";
import { OfferAddedModal } from "@/pages/events/features/notifications/components/offer-added-modal";
import { useEventRepository } from "@/pages/events/hooks/use-event-repository";

// Types
export type EventUpdatesType =
  | "event_added_v2"
  | "venue_changed"
  | "lineup_added"
  | "offer_added_v2"
  | "event_cancelled"
  | "tickets_on_sale"
  | "date_changed";

export type EventUpdatesAttributes = {
  artistIds?: string[];
  venueId?: string;
  followerTypes?: string[];
  regions?: string[];
  similarEventIds?: string[];
  genreIds?: string[];
  excludeGenreIds?: string[];
};
export interface Artist {
  id: string;
  name: string;
  followers?: number;
}

export interface EventUpdatesContextType {
  // State
  isOptionsOpen: boolean;
  isArtistModalOpen: boolean;
  isFollowerModalOpen: boolean;
  isOfferAddedModalOpen: boolean;
  isConfirmationModalOpen: boolean;
  selectedArtists: string[];
  setSelectedArtists: (ids: string[]) => void;
  selectedFollowerTypes: string[];
  artists: Artist[];
  venue: string | undefined;
  pendingUpdate: {
    type: EventUpdatesType;
  } | null;

  // Actions
  openEventUpdatesOptions: () => void;
  closeEventUpdatesOptions: () => void;
  openArtistModal: () => void;
  closeArtistModal: () => void;
  openFollowerModal: () => void;
  closeFollowerModal: () => void;
  openOfferAddedModal: () => void;
  closeOfferAddedModal: () => void;
  openConfirmationModal: () => void;
  closeConfirmationModal: () => void;
  toggleArtist: (artistId: string) => void;
  toggleFollowerType: (followerType: string) => void;
  clearSelectedArtists: () => void;
  clearSelectedFollowerTypes: () => void;
  sendEventUpdates: (
    type: EventUpdatesType,
    options?: EventUpdatesAttributes,
  ) => void;

  // Configuration
  setArtists: (artists: Artist[]) => void;
  setVenue: (venueId: string) => void;
  onEventUpdatesSend?: (
    type: EventUpdatesType,
    options?: { artistIds?: string[] },
  ) => void;
  setPendingUpdate: (pendingUpdate: { type: EventUpdatesType }) => void;
}

const EventUpdatesContext = createContext<EventUpdatesContextType | undefined>(
  undefined,
);

interface EventUpdatesProviderProps {
  children: ReactNode;
  artists?: Artist[];
  onEventUpdatesSend?: (
    type: EventUpdatesType,
    options?: { artistIds?: string[] },
  ) => void;
}

export function EventUpdatesProvider({
  children,
  artists: initialArtists = [],
  onEventUpdatesSend,
}: EventUpdatesProviderProps) {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isArtistModalOpen, setIsArtistModalOpen] = useState(false);
  const [selectedArtists, setSelectedArtists] = useState<string[]>([]);
  const [isFollowerModalOpen, setIsFollowerModalOpen] = useState(false);
  const [isOfferAddedModalOpen, setIsOfferAddedModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedFollowerTypes, setSelectedFollowerTypes] = useState<string[]>([
    "artist",
    "venue",
  ]);
  const [artists, setArtists] = useState<Artist[]>(initialArtists);
  const [venue, setVenue] = useState<string>();
  const [pendingUpdate, setPendingUpdate] = useState<{
    type: EventUpdatesType;
  } | null>(null);

  const { id } = useParsed();
  const { triggerBusinessEvent } = useEventRepository();

  const openEventUpdatesOptions = () => setIsOptionsOpen(true);
  const closeEventUpdatesOptions = () => setIsOptionsOpen(false);

  const openArtistModal = () => {
    setIsArtistModalOpen(true);
    setIsOptionsOpen(false);
  };

  const closeArtistModal = () => {
    setIsArtistModalOpen(false);
    setSelectedArtists([]);
    closeConfirmationModal();
  };

  const openFollowerModal = () => {
    setIsFollowerModalOpen(true);
    setIsOptionsOpen(false);
  };

  const closeFollowerModal = () => {
    setIsFollowerModalOpen(false);
    closeConfirmationModal();
  };

  const openOfferAddedModal = () => {
    setIsOfferAddedModalOpen(true);
    setIsOptionsOpen(false);
  };

  const closeOfferAddedModal = () => {
    setIsOfferAddedModalOpen(false);
    closeConfirmationModal();
  };

  const openConfirmationModal = () => setIsConfirmationModalOpen(true);
  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setPendingUpdate(null);
  };

  const toggleArtist = (artistId: string) => {
    setSelectedArtists((prev) =>
      prev.includes(artistId)
        ? prev.filter((id) => id !== artistId)
        : [...prev, artistId],
    );
  };

  const clearSelectedArtists = () => setSelectedArtists([]);

  const toggleFollowerType = (followerType: string) => {
    setSelectedFollowerTypes((prev) =>
      prev.includes(followerType)
        ? prev.filter((type) => type !== followerType)
        : [...prev, followerType],
    );
  };

  const clearSelectedFollowerTypes = () => setSelectedFollowerTypes([]);

  const sendEventUpdates = async (
    type: EventUpdatesType,
    options?: EventUpdatesAttributes,
  ) => {
    // Call the custom handler if provided
    if (onEventUpdatesSend) {
      onEventUpdatesSend(type, options);
    } else {
      switch (type) {
        case "lineup_added":
          if (!options?.artistIds) {
            openArtistModal();
            return;
          }
          break;
        case "event_added_v2":
          if (!options?.followerTypes) {
            openFollowerModal();
            return;
          }
          break;
        case "offer_added_v2":
          if (
            !options?.artistIds &&
            !options?.similarEventIds &&
            !options?.genreIds
          ) {
            openOfferAddedModal();
            return;
          }
        default:
          break;
      }
      // Default behavior
      console.debug("Sending notification:", { type, ...options });
      delete options?.followerTypes;
      triggerBusinessEvent(id!.toString(), type, options);
    }

    // Close modals and reset state
    setIsOptionsOpen(false);
    setIsArtistModalOpen(false);
    setSelectedArtists([]);
    setIsFollowerModalOpen(false);
    setSelectedFollowerTypes([]);
  };

  const value: EventUpdatesContextType = {
    // State
    isOptionsOpen,
    isArtistModalOpen,
    isFollowerModalOpen,
    isOfferAddedModalOpen,
    isConfirmationModalOpen,
    selectedArtists,
    setSelectedArtists,
    selectedFollowerTypes,
    artists,
    venue,
    pendingUpdate,

    // Actions
    openEventUpdatesOptions,
    closeEventUpdatesOptions,
    openArtistModal,
    closeArtistModal,
    openFollowerModal,
    closeFollowerModal,
    openOfferAddedModal,
    closeOfferAddedModal,
    openConfirmationModal,
    closeConfirmationModal,
    toggleArtist,
    toggleFollowerType,
    clearSelectedArtists,
    clearSelectedFollowerTypes,
    sendEventUpdates,

    // Configuration
    setArtists,
    setVenue,
    onEventUpdatesSend,
    setPendingUpdate,
  };

  return (
    <EventUpdatesContext.Provider value={value}>
      {children}
      <ArtistSelectionModal />
      <FollowerGroupSelectionModal />
      <UpdateConfirmationModal />
      <OfferAddedModal />
    </EventUpdatesContext.Provider>
  );
}

export function useEventUpdates() {
  const context = useContext(EventUpdatesContext);
  if (context === undefined) {
    throw new Error(
      "useEventUpdates must be used within a EventUpdatesProvider",
    );
  }
  return context;
}
